const auth = {
	activate: async function (body) {
		try {
			let res = await fetch('/account/activate', {
				method: 'POST',
				body
			});

			if (res && !res.ok) {
				throw new Error(res.status);
			}

			return {
				data: true
			};
		} catch (error) {
			return { error };
		}
	},
	register: async function (body) {
		try {
			let res = await fetch('/account', {
				method: 'POST',
				body
			});

			if (res && !res.ok) {
				throw new Error(res.status);
			}

			return {
				data: true
			};
		} catch (error) {
			return { error };
		}
	},
	login: async function (body) {
		try {
			let res = await fetch('/account/login', {
				method: 'POST',
				body
			});

			if (res && !res.ok) {
				throw new Error(res.status);
			}

			return {
				data: true
			};
		} catch (error) {
			return { error };
		}
	},
	recover: async function (body) {
		try {
			let res = await fetch('/account/recover', {
				method: 'POST',
				body
			});

			if (res && !res.ok) {
				throw new Error(res.status);
			}

			return {
				data: true
			};
		} catch (error) {
			return { error };
		}
	},
	reset: async function (body) {
		try {
			let res = await fetch('/account/reset', {
				method: 'POST',
				body
			});

			if (res && !res.ok) {
				throw new Error(res.status);
			}

			return {
				data: true
			};
		} catch (error) {
			return { error };
		}
	}
};

/**
 * Conducts search via API
 * @param  { string } id - id of form based on index of position in document
 */
const search = {
	get: async function (value, limit = 10, section = 'predictive-search') {
		try {
			const res = await fetch(
				`/search/suggest?q=${value}&&resources[limit]=${limit}&resources[options][fields]=body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor&section_id=${section}`
			);

			if (res && !res.ok) {
				throw new Error(res.status);
			}

			const text = await res.text();

			const html = new DOMParser()
				.parseFromString(text, 'text/html')
				.querySelector('#search-results');

			if (!html) return { data: null };

			return {
				data: html.innerHTML
			};
		} catch (error) {
			return { error };
		}
	}
};

const cart = {
	/**
	 * Add product to cart based on variant ID
	 * @params { string } variantID - ID of variant to add to cart
	 * @params { number } quantity - amount of product variant to add
	 * @params { string|number } selling_plan - app based selling plan
	 * @return { object|error } returns object of variant with amount in cart
	 * */
	add: async function (variantID, quantity, selling_plan = null) {
		try {
			if (!variantID || !quantity) return;

			let body = {
				id: variantID,
				quantity,
				selling_plan
			};

			const res = await fetch(`/cart/add.js`, {
				method: 'POST',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					'X-Requested-With': 'xmlhttprequest'
				},
				body: JSON.stringify(body)
			});

			const data = await res.json();

			if (!res || !res.ok) {
				return { error: 'Request could not be completed' };
			}

			return { data };
		} catch (error) {
			return { error };
		}
	},
	/**
	 * Updates lineitems in cart based on variant IDs
	 * @params { string } variantID - ID of variant to add to cart
	 * @params { number } quantity - amount of product variant to add
	 * @params { object } properties - key value pairs of options to add to a line item
	 * @params { object } attributes - key value pairs of options to add to cart attributes
	 * @return { object|error } returns object of variant with amount in cart
	 * */
	update: async function (variantID, quantity, properties, attributes) {
		try {
			let body = {};

			if (variantID) body.id = variantID;
			if (quantity) body.quantity = quantity;
			if (attributes) body.attributes = attributes;

			if (properties && properties.Initials && properties.Style) {
				body.properties = {
					Initials: properties.Initials
				};
			}

			if (properties && properties.To) {
				body.properties = {
					gift: 'yes',
					From: properties.From,
					Message: properties.Message,
					'Recipient name': '',
					'Recipient email': properties.To,
					'To': properties.To,
					'__shopify_send_gift_card_to_recipient': 'on'
				};
			}

			if (properties && properties._LPROP && body.properties) body.properties._LPROP = 'Final Sale';
			if (properties && properties._LPROP && !body.properties) {
				body.properties = { _LPROP: 'Final Sale' };
			}

			const res = await fetch(`/cart/update.js`, {
				method: 'POST',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					'X-Requested-With': 'xmlhttprequest'
				},
				body: JSON.stringify(body)
			});

			const data = await res.json();

			if (!res || !res.ok) {
				return { error: 'Request could not be completed' };
			}

			return { data };
		} catch (error) {
			return { error };
		}
	},
	/**
	 * Updates lineitem in cart based on variant IDs
	 * @params { object } changes - object of lineitems with variant id as key and quantity as value
	 * @return { object|error } returns object of variant with amount in cart
	 * */
	change: async function (changes) {
		try {
			if (!changes || (changes && Object.keys(changes).length === 0)) return;

			const res = await fetch(`/cart/change.js`, {
				method: 'POST',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					'X-Requested-With': 'xmlhttprequest'
				},
				body: JSON.stringify(changes)
			});

			const data = await res.json();

			if (!res || !res.ok) {
				return { error: 'Request could not be completed' };
			}

			return { data };
		} catch (error) {
			return { error };
		}
	},
	/**
	 * Updates cart note
	 * @params { string } note - string value of note
	 * @return { object|error } returns object of variant with amount in cart
	 * */
	notes: async function (note) {
		try {
			const res = await fetch(`/cart/update.js`, {
				method: 'POST',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					'X-Requested-With': 'xmlhttprequest'
				},
				body: JSON.stringify({
					note
				})
			});

			const data = await res.json();

			if (!res || !res.ok) {
				return { error: 'Request could not be completed' };
			}

			return { data };
		} catch (error) {
			return { error };
		}
	},
	/**
	 * Get current users cart items
	 * @return { object|error } returns object of cart items
	 * */
	get: async function () {
		try {
			const res = await fetch(`/cart.js`, {
				method: 'GET',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					'X-Requested-With': 'xmlhttprequest'
				}
			});

			const data = await res.json();

			if (!res || !res.ok) {
				return { error: 'Request could not be completed' };
			}

			return { data };
		} catch (error) {
			return { error };
		}
	}
};

const product = {
	get: async function (handle) {
		try {
			const res = await fetch(`/products/${handle}.js`, {
				method: 'GET',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					'X-Requested-With': 'xmlhttprequest'
				}
			});

			const data = await res.json();

			if (!res || !res.ok) {
				return { error: 'Request could not be completed' };
			}

			return { data };
		} catch (error) {
			return { error };
		}
	},

	recommendations: async function (id, limit = 4) {
		try {
			const res = await fetch(
				`/recommendations/products?product_id=${id}&limit=${limit}&section_id=product-related&intent=related`,
				{
					method: 'GET',
					credentials: 'same-origin',
					headers: {
						'Content-Type': 'application/json',
						'X-Requested-With': 'xmlhttprequest'
					}
				}
			);

			const data = await res.text();

			if (!res || !res.ok) {
				return { error: 'Request could not be completed' };
			}

			return { data };
		} catch (error) {
			return { error };
		}
	}
};

const store = {
	get: async function (endpoint) {
		try {
			const res = await fetch(endpoint, {
				method: 'GET',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					'X-Requested-With': 'xmlhttprequest'
				}
			});

			const data = await res.text();

			if (!res || !res.ok) {
				return { error: 'Request could not be completed' };
			}

			return { data };
		} catch (error) {
			return { error };
		}
	}
};

const collection = {
	filter: async function (endpoint) {
		try {
			const res = await fetch(endpoint, {
				method: 'GET',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					'X-Requested-With': 'xmlhttprequest'
				}
			});

			if (!res || !res.ok) {
				return { error: 'Request could not be completed' };
			}

			const data = await res.text();

			return { data };
		} catch (error) {
			return { error };
		}
	}
};

Solver.api.cart = cart;
Solver.api.collection = collection;
Solver.api.product = product;
Solver.api.search = search;
Solver.api.store = store;
